<script setup lang="ts">
import { computed } from "vue";

import { getAlmInfo } from "@/backend/read/BackendReader";
import { cardKey } from "@/components/StickyNote/injectKeys";
import { boardKey } from "@/components/board/injectKeys";
import MirrorStickyChip from "@/components/chip/MirrorStickyChip.vue";
import StickyLinksChip from "@/components/chip/StickyLinksChip.vue";
import StickyStatusChip from "@/components/chip/StickyStatusChip.vue";
import StoryPointsChip from "@/components/chip/StoryPointsChip.vue";
import UserChip from "@/components/chip/UserChip/UserChip.vue";
import WSJFChip from "@/components/chip/WSJFChip.vue";
import { isBacklogBoard, isPlanningBoard, isRiskBoard, isTeamBoard } from "@/model/board";
import { useBoardStore } from "@/store/board";
import { isStickyTypeAlmSynced } from "@/store/stickyType";
import { useTeamStore } from "@/store/team";
import { useWorkModeStore } from "@/store/workMode";
import { injectStrict } from "@/utils/vue";

import StickyNoteRow from "../../../StickyNoteRow.vue";
import ArtSelectChip from "./internal/ArtSelectChip/ArtSelectChip.vue";
import TeamSelectChip from "./internal/TeamSelectChip.vue";

const card = injectStrict(cardKey);
const board = injectStrict(boardKey);

const isSelected = computed(() => useBoardStore().activeCardId === card.id);

const isAssigneeVisible = computed(
  () =>
    (!isStickyTypeAlmSynced(card.type) || getAlmInfo().hasAssignee) &&
    (card.type.origin === "team" || card.type.origin === "risk"),
);
const teamName = computed(() => {
  const teams = useTeamStore().teamsInCurrentArt;
  return teams.find((team) => team.id === card.teamId)?.name;
});

const isTeamNameVisible = computed(() => isRiskBoard(board.value.type));
const isTeamSelectChipVisible = computed(() => board.value.type === "backlog");
const isArtSelectChipVisible = computed(() => board.value.type === "solution_backlog");

const isLinksChipVisible = computed(() => {
  // visible only when selected or on the planning/team board
  return isPlanningBoard(board.value.type) || isTeamBoard(board.value.type) || isSelected.value;
});

// visible only if sticky is selected
const isMirrorChipVisible = computed(() => isSelected.value);

const isWSJFChipVisible = computed(() => {
  // hidden on the team board and risk board
  if (isTeamBoard(board.value.type) || isRiskBoard(board.value.type)) {
    return false;
  }
  // hidden for execution mode
  if (useWorkModeStore().isExecutionMode) {
    return false;
  }
  return (
    // visible on all backlog boards
    isBacklogBoard(board.value.type) ||
    // visible only for stickies that we created on the backlog board
    isBacklogBoard(card.type.origin)
  );
});

const isStatusChipVisible = computed(() => {
  // visible only in execution mode
  return useWorkModeStore().isExecutionMode;
});

const isStoryPointsChipVisible = computed(() => {
  // hidden in execution mode
  if (useWorkModeStore().isExecutionMode) return false;
  // visible only for work items created on the team board
  return card.type.origin === "team";
});
</script>

<template>
  <StickyNoteRow class="workitem-footer">
    <UserChip v-if="isAssigneeVisible" />
    <span v-if="isTeamNameVisible && teamName" class="team-name">
      {{ teamName }}
    </span>
    <TeamSelectChip v-if="isTeamSelectChipVisible" shrink />
    <ArtSelectChip v-if="isArtSelectChipVisible" shrink />
    <StickyLinksChip
      v-if="isLinksChipVisible"
      :linking-card="{ type: 'sticky', id: card.id }"
      show-upper-level-links
    />
    <MirrorStickyChip v-if="isMirrorChipVisible" />

    <div class="divider" />

    <WSJFChip v-if="isWSJFChipVisible" />
    <StoryPointsChip v-if="isStoryPointsChipVisible" />
    <StickyStatusChip v-if="isStatusChipVisible" />
  </StickyNoteRow>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/utils";

.workitem-footer {
  .team-name {
    @include utils.ellipsis;
  }

  .divider {
    margin-left: auto;
    margin-right: -6px;
  }
}
</style>
